import { cloneDeep } from "lodash";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import ConnectLinks from "../components/shared/connectLinks";
import Logo from "../images/gvi-logo2.inline.svg";
import CalloutArrow from "../images/icons/callout-icon.inline.svg";
import CalloutLightBulb from "../images/icons/callout-light-bulb.inline.svg";
import CloseSVG from "../images/icons/close.inline.svg";
import MenuBurger from "../images/icons/menu-burger.inline.svg";
import PhoneDark from "../images/icons/phone-icon-dark.inline.svg";
import PhoneLight from "../images/icons/phone-icon-light.inline.svg";
import { Link } from "../utils/customLink";
import { flattenMenu } from "../utils/flattenMenu";
import MenuLocations from "./menu/menuLocations";
import MenuOnline from "./menu/menuOnline";
import MenuProgram from "./menu/menuPrograms";
import MenuWhyGVI from "./menu/menuWhyGVI";
import MobileMenu from "./menu/mobileMenu";
import FloatingSiteSuggest from "./widgets/floatingSiteSuggest";

function SiteNavToggle({
  siteMetadata,
  siteSwitchDesc,
  to,
  siteCalloutClass,
  toggleDesc
}) {
  return (
    <div className="brand-things-container sm:h-full flex m-auto xxs:mr-auto xxs:ml-7">
      <div className="brand group h-full flex z-10 order-2 planet:order-1">
        <div className="flex items-center justify-center">
          <Link to="/">
            <div className="h7 menu-link text-generic-text planet:text-white !text-xs sm:!text-sm !font-semibold uppercase p-[2px] bg-gradient-to-b from-[#F6F9FC] to-[#ECF1F3] planet:from-[#696969] planet:to-[#505050] rounded-xxs shadow-calloutOuter">
              <div
                className="bg-[#D1D6D9] planet:bg-[#2F2F2F] xxxs:px-2 sm:px-5 py-1 rounded-xxs block"
                dangerouslySetInnerHTML={{ __html: siteMetadata?.nav_title }}
              />
            </div>
          </Link>
        </div>
      </div>
      <div className="brand group h-full flex -ml-1.5 z-0 relative order-1 planet:order-2">
        <div className="flex items-center justify-center">
          <div className="flex items-center justify-center">
            <Link to={to}>
              <div className="h7 menu-link text-[#D7DDE0] planet:text-[#565656] !text-xs sm:!text-sm uppercase p-[2px] bg-gradient-to-b from-[#D9E1E7] to-[#D9E1E7] planet:from-[#2D2D2D] planet:to-[#424242] rounded-r-xxs shadow-calloutInner">
                <div
                  className="bg-[#F7F8FA] planet:bg-black xxxs:px-2 sm:px-5 px-5 py-0.5 rounded-r-xxs block"
                  dangerouslySetInnerHTML={{
                    __html: siteMetadata?.sec_nav_title
                  }}
                />
              </div>
            </Link>
          </div>
        </div>

        <div
          className={`block !absolute -bottom-20 -m-[50%] z-10 items-center tooltip text-generic-text planet:text-white h-32 ${siteCalloutClass}`}
        >
          <CalloutArrow className="fill-white planet:fill-secondary-bg h-8 w-auto m-auto -mb-3.5" />
          <div className="inner-container relative grid grid-rows-2 grid-cols-12 gap-0 p-4 rounded-sm bg-white planet:bg-secondary-bg">
            <div className="col-span-2 flex items-center justify-center">
              <CalloutLightBulb
                className={"fill-generic-text planet:fill-white w-full h-full"}
              />
            </div>
            <div className="uppercase font-semibold col-span-10 flex items-center justify-center text-sm">
              {toggleDesc}
            </div>

            <div className="col-span-full leading-wider tracking-wide flex justify-center items-center py-1 px-2 planet:text-[#565656]">
              {siteSwitchDesc}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Nav = ({
  site,
  headerMenu,
  brandingOpts,
  globalFileLinks,
  applyNow = "/application-form/",
  applyLabel = "Apply",
  enquireNow = "/enquiry-form/",
  enquireLabel = "Enquire",
  isGroupPage = false
}) => {
  const [isMobileOpen, setMobileOpen] = useState(false);
  const {
    colorMode,
    currentBrand,
    currentSiteMeta,
    planetDomain,
    peopleDomain
  } = brandingOpts;

  //console.debug("brandingOpts", brandingOpts);

  const [hoveredLink, setHovered] = useState("");
  const [siteCallout, setSiteCallout] = useState(
    "invisible group-hover:visible"
  );

  const [siteCalloutDisplayed, setSiteCalloutDisplayStatus] = useState(
    (typeof window !== "undefined" &&
      window.localStorage.getItem("displayCallout") &&
      window.localStorage.getItem("displayCallout") === "false") ??
      false
  );
  const [isSuggestBannerActive, setSuggestBannerActive] = useState(false);

  const toggleMenu = () => {
    if (isMobileOpen) {
      document
        .getElementsByTagName("html")[0]
        .classList.remove("disable-scrolling");
    } else {
      document
        .getElementsByTagName("html")[0]
        .classList.add("disable-scrolling");
    }
    setMobileOpen(!isMobileOpen);
  };

  const intlNumber = currentSiteMeta?.contact_details?.international ?? "";
  const secondarySiteUrl = currentBrand.includes("people")
    ? planetDomain
    : peopleDomain;

  const srcMenuItems = headerMenu?.menuItems?.edges?.flatMap(
    (menuItem) => menuItem.node
  );

  const restructureMenu = flattenMenu(srcMenuItems, { idKey: "id" });
  const headerTreeMenu = headerMenu?.menuItems?.nodes ?? [];

  restructureMenu?.forEach((menuItem, i) => {
    const path = menuItem.path ?? menuItem.url;
    headerTreeMenu.push({
      ...menuItem,
      title: menuItem.label,
      url: path
    });
  });

  const rawMenuItems = headerTreeMenu ?? [];

  const menuItemsDesktop = rawMenuItems.filter(
    (menu) => menu.parentId === null
  );

  const menuItems = cloneDeep(menuItemsDesktop);

  useEffect(() => {
    const url = new URL(window.location);

    document
      .getElementsByTagName("html")[0]
      .classList.remove("disable-scrolling");

    if (url?.pathname === "/" && siteCalloutDisplayed === false) {
      setSiteCallout("animate-bouncesm");
      setSiteCalloutDisplayStatus(true);

      if (typeof window !== "undefined") {
        window.localStorage.setItem("displayCallout", "false");
      }
    }

    //hide the callout notification
    const calloutTimer = setTimeout(() => {
      setSiteCallout("invisible group-hover:visible");
    }, 5000);

    return () => {
      clearTimeout(calloutTimer);
    };
  }, [siteCalloutDisplayed]);

  if (!menuItems || menuItems.length === 0) return null;

  const siteSwitchDesc = currentBrand.includes("people") ? (
    <>Switch to nature conservation programs</>
  ) : (
    <>Switch to community development programs</>
  );

  const siteToggleDesc = currentBrand.includes("people") ? (
    <>Toggle to Planet</>
  ) : (
    <>Toggle to People</>
  );

  const ConnectLinkBlock = () => (
    <ConnectLinks
      key={"connect-link-block"}
      site={site}
      planetDomain={planetDomain}
      colorMode={colorMode}
      globalFileLinks={globalFileLinks}
    />
  );

  return (
    <>
      <div className="fixed z-50 flex flex-wrap w-screen bg-white place-items-center planet:gvi-black-menu">
        <FloatingSiteSuggest suggestStatus={setSuggestBannerActive} />

        <section className="relative w-full px-8">
          <nav className="flex justify-between h-16 mx-auto ">
            <div className="flex items-center w-full m-auto sm:h-full">
              <Link to="/">
                <Logo className="w-[39px] sm:w-[57px] fill-black-lighter planet:fill-white" />
              </Link>

              <SiteNavToggle
                siteMetadata={site.siteMetadata}
                to={`${secondarySiteUrl}?nav_switcher=1`}
                siteSwitchDesc={siteSwitchDesc}
                siteCalloutClass={siteCallout}
                toggleDesc={siteToggleDesc}
              />

              <ul className="hidden lg:flex ml-auto justify-end items-baseline nav-items mt-2">
                {menuItemsDesktop?.map((menuItem, i) => {
                  const path =
                    menuItem?.connectedNode?.node?.uri ?? menuItem.url;

                  let childMenuItems =
                    menuItem?.children?.nodes ?? menuItem?.children;

                  return (
                    <React.Fragment key={`menu-item-desktop-${i}`}>
                      {menuItem.parentId === null && (
                        <li
                          key={`menu-item-${i}-${menuItem.url}`}
                          className={[
                            hoveredLink === menuItem.id ? "active" : "",
                            isSuggestBannerActive ? "active-sugggest" : "",
                            "nav-item"
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          onMouseEnter={() => setHovered(menuItem.id)}
                          onMouseLeave={() => setHovered("")}
                        >
                          <Link
                            className="nav-item_link gvi-gray-menu-link text-sm font-normal menu-link inline-block relative pb-5 pt-4 px-4 mr-2 rounded-t-[5px]"
                            to={path}
                            activeClassName={
                              "current-menu-item current_page_item"
                            }
                          >
                            {menuItem.label}
                          </Link>
                          {/*{navMenus[menuItem.label]}*/}
                          {(menuItem.label.toLowerCase() ===
                            "programs abroad" ||
                            menuItem.label.toLowerCase() ===
                              "programs overseas") && (
                            <MenuProgram
                              menuItems={childMenuItems}
                              menuSectionTitle={"Individual programs"}
                              site={site}
                              colorMode={colorMode}
                              currentBrand={currentBrand}
                              peopleDomain={peopleDomain}
                              planetDomain={planetDomain}
                            >
                              <ConnectLinkBlock />
                            </MenuProgram>
                          )}
                          {menuItem.label.toLowerCase() ===
                            "virtual & online" && (
                            <MenuOnline
                              menuItems={childMenuItems}
                              site={site}
                              colorMode={colorMode}
                              planetDomain={planetDomain}
                            >
                              <ConnectLinkBlock />
                            </MenuOnline>
                          )}
                          {menuItem.label.toLowerCase() === "why gvi" && (
                            <MenuWhyGVI
                              menuItems={childMenuItems}
                              site={site}
                              planetDomain={planetDomain}
                            >
                              <ConnectLinkBlock />
                            </MenuWhyGVI>
                          )}
                          {menuItem.label.toLowerCase() === "locations" && (
                            <MenuLocations
                              menuItems={childMenuItems}
                              site={site}
                              planetDomain={planetDomain}
                            >
                              <ConnectLinkBlock />
                            </MenuLocations>
                          )}
                        </li>
                      )}
                    </React.Fragment>
                  );
                })}
              </ul>
              <div
                className={`menu-buttons flex grow-[0.5] ${
                  isGroupPage === false ? "max-w-[14rem]" : "max-w-[16rem]"
                }`}
              >
                {enquireNow?.length < 2 && applyNow?.length < 2 && (
                  <div className="w-full flex flex-1 h-16" />
                )}
                {enquireNow?.length > 2 && (
                  <Link
                    className={`flex tracking-[0.04em] font-bold !text-white ${
                      isGroupPage === false ? "uppercase" : ""
                    }`}
                    to={enquireNow}
                  >
                    <div
                      className={`text-base planet:font-body font-bold items-center justify-center hidden h-16 space-x-5 px-5 cursor-pointer bg-yellow-standard hover:bg-yellow-light hover:gvi-box-shadow planet:gvi-gray-bg grow basis-0 xl:flex planet:hover:bg-gray-darkest ${
                        isGroupPage === false ? "" : "w-32"
                      }`}
                      dangerouslySetInnerHTML={{ __html: enquireLabel }}
                    />
                  </Link>
                )}
                {applyNow?.length > 2 && (
                  <Link
                    className={`flex tracking-[0.04em] font-bold !text-white planet:!text-black ${
                      isGroupPage === false ? "uppercase" : ""
                    }`}
                    to={applyNow}
                  >
                    <div
                      className={`text-base planet:font-body font-bold items-center justify-center hidden h-16 space-x-5 cursor-pointer bg-red-light hover:bg-black-lighter hover:gvi-box-shadow planet:gvi-bg-green grow basis-0 xl:flex planet:hover:bg-white ${
                        isGroupPage === false ? "px-8" : "w-32 px-5"
                      }`}
                      dangerouslySetInnerHTML={{ __html: applyLabel }}
                    />
                  </Link>
                )}
              </div>

              <div className="items-center hidden space-x-5 xl:flex">
                <a
                  className="flex flex-col items-center pl-5 text-xs font-normal hover:text-gray-500 text-gray-darken"
                  href={`tel:${intlNumber}`}
                >
                  <div className="static group flex items-center">
                    {colorMode === "light" ? (
                      <PhoneLight className="w-5 h-5 mx-auto fill-gray-darken group-hover:fill-black-lighter" />
                    ) : (
                      <PhoneDark className="w-5 h-5 mx-auto fill-gray-darken group-hover:fill-white" />
                    )}
                    <div className="hidden xl:inline-block menu-link font-normal group-hover:font-bold text-[11px] pt-0.5 pl-1 group-hover:text-black-lighter planet:group-hover:text-white">
                      Call Us
                    </div>
                  </div>
                  <div className="hidden xl:inline-block  menu-link gvi-gray-menu-link font-normal group-hover:font-bold text-[11px] pt-1 group-hover:text-black-lighter planet:group-hover:text-white">
                    {intlNumber}
                  </div>
                </a>
              </div>
            </div>
            <Link
              className="self-center mr-2 navbar-burger xl:hidden"
              to="#"
              onClick={toggleMenu}
            >
              {/* mobile menu */}
              {isMobileOpen ? (
                <CloseSVG className="stroke-black-lighter planet:stroke-white" />
              ) : (
                <MenuBurger className="stroke-black-lighter planet:stroke-white" />
              )}
            </Link>
          </nav>
          <MobileMenu
            isOpen={isMobileOpen}
            menuItems={menuItems}
            site={site}
            phoneNumber={intlNumber}
            colorMode={colorMode}
            secondarySiteUrl={secondarySiteUrl}
          />
        </section>
      </div>
    </>
  );
};

Nav.propTypes = {
  site: PropTypes.object,
  headerMenu: PropTypes.object
};

SiteNavToggle.propTypes = {
  siteMetadata: PropTypes.any,
  to: PropTypes.string,
  siteSwitchDesc: PropTypes.any
};
export default React.memo(Nav);
